import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import {
  FormField,
  FormFieldEditableBy,
  FormFieldType,
  FormPageSize,
  FormSigner,
  FormTemplateSettings,
} from "../../../../interfaces/DTO/forms/formTemplate";
import { FormValue } from "../../../../interfaces/DTO/forms/taskForm";
import { boxToScreen, choiceGroup, dateFormatParts, formatDateValue, parseDateValue } from "../../../admin/formTemplates/formFieldGeometry";

const isChecked = (v: FormValue | undefined) =>
  v === true || (typeof v === "string" && ["true", "1", "yes", "כן", "x", "v"].includes(v.trim().toLowerCase()));

/**
 * Where a text sits in its box - as the server draws it: the field's own alignment, else the form's; auto: Hebrew
 * on the right and anything else (numbers, English) on the left. An empty box waits on the right (Hebrew is typed most).
 */
const textAlignOf = (field: FormField, value: unknown, settings: FormTemplateSettings): "left" | "right" | "center" => {
  const align = field.align ?? settings.align;
  if (align === "left" || align === "right" || align === "center") return align;
  const text = value == null ? "" : String(value);
  return !text || /[\u0590-\u05FF]/.test(text) ? "right" : "left";
};

interface Props {
  imageUrl: string | null;
  page: number;
  pageSize: FormPageSize;
  fields: FormField[]; // expanded (rows are separate fields)
  values: Record<string, FormValue>;
  readOnly: boolean;
  agentSigned: boolean;
  settings: FormTemplateSettings; // the form's font size / color - a field's own wins
  onChange: (changes: Record<string, FormValue>) => void;
}

/**
 * Editing on the page: the blank template page as the background and inputs over it at the fields' boxes
 * (scaled to the width). Instant - but a browser overlay may differ slightly from the PDF; the server's render
 * is what gets signed (checked before sending).
 */
const TaskFormPage: React.FC<Props> = ({ imageUrl, page, pageSize, fields, values, readOnly, agentSigned, settings, onChange }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  // the date being typed: the agent's text as is (shown in the field's format when not typing)
  const [dateDraft, setDateDraft] = useState<{ key: string; text: string } | null>(null);
  const scale = width > 0 ? width / pageSize.width : 0;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  const chooseInGroup = (field: FormField) => {
    const group = choiceGroup(field);
    const selected = values[field.key] != null && String(values[field.key]) === field.choice_value;
    const changes: Record<string, FormValue> = {};
    for (const f of fields) {
      if (f.type === FormFieldType.CHOICE && choiceGroup(f) === group) {
        changes[f.key] = selected ? null : (field.choice_value ?? null);
      }
    }
    onChange(changes);
  };

  const inputSx = (fontPx: number, color = settings.font_color) => ({
    position: "absolute" as const,
    color,
    // text: purple (checkboxes: green, below)
    border: "1px solid rgba(123,31,162,0.4)",
    background: "rgba(243,229,245,0.55)",
    font: `${fontPx}px Arial, sans-serif`,
    padding: "0 2px",
    boxSizing: "border-box" as const,
    outline: "none",
  });

  return (
    <Box ref={ref} sx={{ position: "relative", width: "100%", height: pageSize.height * scale || 600, bgcolor: "#fff", boxShadow: 2, direction: "ltr" }}>
      {imageUrl ? (
        <img src={imageUrl} alt={`page ${page}`} draggable={false}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />
      ) : (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}

      {scale > 0 && fields.filter((f) => f.page === page).map((field) => {
        const r = boxToScreen(field.box, pageSize.height, scale);
        const fontPx = Math.max(9, Math.min((field.font_size ?? settings.font_size) * scale, r.height * 0.8));
        const color = field.font_color ?? settings.font_color;
        const value = values[field.key];
        const title = [field.label || field.key, field.editable_by === FormFieldEditableBy.CLIENT ? "(הלקוח יכול לשנות בחתימה)" : ""]
          .filter(Boolean).join(" ");
        const clientField = field.editable_by === FormFieldEditableBy.CLIENT;
        const border = clientField ? "1px dashed #ef6c00" : undefined;

        if (field.type === FormFieldType.SIGNATURE || field.type === FormFieldType.IMAGE) {
          const isAgent = field.signer === FormSigner.AGENT;
          return (
            <Box key={field.key} title={title} sx={{
              position: "absolute", ...r, border: "1px dashed", borderColor: isAgent ? "#7b1fa2" : "#d32f2f",
              bgcolor: isAgent && agentSigned ? "rgba(46,125,50,0.12)" : "rgba(211,47,47,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "text.secondary",
              direction: "rtl", pointerEvents: "none",
            }}>
              {isAgent ? (agentSigned ? "✓ חתימת בעל הרישיון" : "חתימת בעל הרישיון") : "חתימת הלקוח"}
            </Box>
          );
        }

        // the date of a signer is set when they sign
        if (field.signer && field.type === FormFieldType.DATE) {
          return (
            <Box key={field.key} title="ממולא בזמן החתימה" sx={{
              position: "absolute", ...r, border: "1px dashed #bbb", bgcolor: "rgba(0,0,0,0.03)", pointerEvents: "none",
            }} />
          );
        }

        if (field.type === FormFieldType.CHECKBOX || field.type === FormFieldType.CHOICE) {
          const checked = field.type === FormFieldType.CHECKBOX
            ? isChecked(value)
            : value != null && String(value) === field.choice_value; // a number (product type 4) as text
          // a checkbox green, a choice box (one of a group) yellow-green
          const choice = field.type === FormFieldType.CHOICE;
          return (
            <Box
              key={field.key}
              title={title}
              onClick={() => {
                if (readOnly) return;
                if (field.type === FormFieldType.CHECKBOX) onChange({ [field.key]: !checked });
                else chooseInGroup(field);
              }}
              sx={{
                position: "absolute", ...r, cursor: readOnly ? "default" : "pointer",
                border: border ?? (choice ? "1.5px solid #827717" : "1px solid rgba(46,125,50,0.6)"),
                bgcolor: choice ? "rgba(230,238,156,0.55)" : "rgba(232,245,233,0.6)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: Math.max(10, r.height * 0.9), lineHeight: 1, fontWeight: 700, color: choice ? "#5d5a10" : "#1b5e20",
              }}
            >
              {checked ? "✓" : ""}
            </Box>
          );
        }

        // a date is typed as plain text (no date picker) and kept as ISO once it is a date - shown in the field's
        // format, laid out as the server draws it (split parts / digit cells)
        const raw = value == null ? "" : String(value);
        const isDate = field.type === FormFieldType.DATE;
        const iso = isDate ? parseDateValue(raw) : null;
        const typing = isDate && dateDraft?.key === field.key;
        const text = typing ? dateDraft!.text : iso ? formatDateValue(iso, field.date_format, field.cells) : raw;
        const dateInput = isDate ? {
          onFocus: () => setDateDraft({ key: field.key, text: iso ? formatDateValue(iso, field.date_format) : raw }),
          onBlur: () => setDateDraft(null),
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            setDateDraft({ key: field.key, text: e.target.value });
            onChange({ [field.key]: parseDateValue(e.target.value) ?? (e.target.value || null) });
          },
        } : {};

        if (isDate && field.date_parts) {
          const dp = field.date_parts;
          const parts = [dp.day, dp.month, dp.year].map((b) => boxToScreen(b, pageSize.height, scale));
          if (typing) {
            // one text input over the whole area of the split boxes
            const left = Math.min(...parts.map((p) => p.left));
            const right = Math.max(...parts.map((p) => p.left + p.width));
            return (
              <input
                key={field.key} type="text" dir="ltr" title={title} value={text} autoFocus {...dateInput}
                style={{
                  ...inputSx(fontPx, color), left, top: parts[0].top, width: right - left, height: parts[0].height,
                  border: border ?? inputSx(fontPx).border, textAlign: "center",
                }}
              />
            );
          }
          // day, month, year in their boxes (the year in the field's format: 4 or 2 digits), a digit per cell if split
          const shortYear = dateFormatParts(field.date_format).shortYear;
          const texts = iso ? [iso.slice(8, 10), iso.slice(5, 7), shortYear ? iso.slice(2, 4) : iso.slice(0, 4)] : null;
          return (
            <React.Fragment key={field.key}>
              {parts.map((p, i) => {
                const partPx = Math.max(9, Math.min((field.font_size ?? settings.font_size) * scale, p.height * 0.8));
                const cellCount = dp.digit_cells ? (i === 2 && !shortYear ? 4 : 2) : 1;
                const partText = texts ? texts[i] : i === 0 ? raw : "";
                return (
                  <Box
                    key={i} title={title}
                    onClick={() => { if (!readOnly) setDateDraft({ key: field.key, text: iso ? formatDateValue(iso, field.date_format) : raw }); }}
                    sx={{
                      ...inputSx(partPx, color), ...p, border: border ?? inputSx(partPx).border, display: "flex", padding: 0,
                      cursor: readOnly ? "default" : "text", whiteSpace: "nowrap", overflow: texts ? "hidden" : "visible",
                    }}
                  >
                    {cellCount > 1 && texts
                      ? Array.from({ length: cellCount }, (_, c) => (
                        <Box key={c} sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>{partText[c] ?? ""}</Box>
                      ))
                      : <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>{partText}</Box>}
                  </Box>
                );
              })}
            </React.Fragment>
          );
        }

        // a fixed text - the template's, not editable
        if (field.fixed_value) {
          return (
            <input
              key={field.key} type="text" dir="auto" title={title} value={field.fixed_value} disabled
              style={{ ...inputSx(fontPx, color), ...r, textAlign: textAlignOf(field, field.fixed_value, settings) }}
            />
          );
        }

        if (field.cells) {
          // comb boxes: one character per cell - a monospace font spaced to the cell's width (close to the server's
          // render, which puts each character in the middle of its cell)
          const cellPx = r.width / field.cells;
          const charPx = Math.min(fontPx, cellPx * 0.9) * 0.6;
          return (
            <input
              key={field.key} type="text" dir="ltr" title={title} maxLength={isDate ? undefined : field.cells}
              value={text} disabled={readOnly}
              onChange={(e) => onChange({ [field.key]: e.target.value })}
              {...dateInput}
              style={{
                ...inputSx(Math.min(fontPx, cellPx * 0.9), color), ...r, border: border ?? inputSx(fontPx).border,
                fontFamily: "monospace", letterSpacing: Math.max(0, cellPx - charPx),
                paddingLeft: Math.max(0, (cellPx - charPx) / 2), paddingRight: 0, textAlign: "left",
              }}
            />
          );
        }

        return (
          <input
            key={field.key} type="text" dir="auto" title={title} value={text} disabled={readOnly}
            onChange={(e) => onChange({ [field.key]: e.target.value })}
            {...dateInput}
            style={{ ...inputSx(fontPx, color), ...r, border: border ?? inputSx(fontPx).border, textAlign: textAlignOf(field, value, settings) }}
          />
        );
      })}
    </Box>
  );
};

export default TaskFormPage;
